import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages/ai-experience-application';
import { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { DiscardScreen } from '@atlassian/generative-ai-modal/screens/Discard';

import {
	cancelStream,
	pauseStream,
	resetSessionState,
	resumeStream,
} from '../../../../pm-plugins/ai-session-context/commands';
import { discardStream } from '../../../../pm-plugins/pure-pm-in-editor-direct-streaming/transaction-builders/buildDiscardTransaction';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useRovoAbort } from '../../../hooks/useRovoDiscard';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { Container } from '../Container';
import { useStreamingScroll } from '../hooks/useStreamingScroll';
import type { Action } from '../hooks/useUIState';
import { useUserDiscard } from '../hooks/useUserDiscard';
import type { SessionUIComponentProps } from '../SessionUIComponent';

export function DiscardSessionScreen({
	api,
	appearance,
	dispatch,
}: Pick<SessionUIComponentProps, 'api' | 'appearance'> & {
	dispatch: React.Dispatch<Action>;
}): React.JSX.Element {
	const { abort } = useRovoAbort();
	const { formatMessage } = useIntl();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const { isInDocumentStreamingEnabled, editorView } = useContentComponentContext();
	const aiDecorationExperiencePluginState = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(states) => states.aiExperienceState?.aiDecorationExperiencePluginState,
	);

	useEffect(() => {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			api?.core?.actions.execute(pauseStream());
		}
	}, [api]);

	const onCancel = useCallback(() => {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			api?.core?.actions.execute(resumeStream());
		} else {
			api?.aiExperience.actions.getAiExperienceActor()?.send({ type: 'disable discard' });
		}
		dispatch({ type: 'cancel' });
	}, [api, dispatch]);

	const onDiscard = useCallback(() => {
		dispatch({ type: 'terminate' });

		if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			const context = api?.aiExperience.actions.getAiExperienceActor().getSnapshot().context;
			const traceIds = aiDecorationExperiencePluginState?.traceId
				? [aiDecorationExperiencePluginState.traceId]
				: context?.traceIds;
			/**
			 * Introduced in EDF-1443 to track discard event confirmations
			 * separately to normal discard events
			 */
			fireAIAnalyticsEvent({
				// Simplified context from streamingApi to avoid issues
				// To follow-up
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.DISMISSED,
					interactionID: context?.channelId,
					experienceName: convertExperienceName(context?.configItem.key.toString() ?? ''),
					isProactive: false,
					invokedFrom: 'streamingApi',
					traceIds: traceIds,
					promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
						? aiDecorationExperiencePluginState?.promptVersion
						: undefined,
				}),
			});
		}

		// Abort any ongoing Rovo requests - this will end the session
		abort();

		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			// Cancel any ongoing non-orchestrator streaming and clear the AI session context
			// This must be done before endAIExperience so the streaming plugin can detect
			// the session was cleared while streaming was in progress
			api?.core?.actions.execute(cancelStream());
			// Then reset the session state
			api?.core?.actions.execute(resetSessionState());
			// Restore original document content before endAIExperience (which clears streaming state)
			if (editorView) {
				discardStream(editorView, true);
			}
		}

		api?.aiExperience.actions.endAIExperience();
	}, [
		api,
		dispatch,
		abort,
		fireAIAnalyticsEvent,
		aiDecorationExperiencePluginState?.traceId,
		aiDecorationExperiencePluginState?.promptVersion,
		editorView,
	]);

	const discardScreenRef = React.useRef<HTMLDivElement | null>(null);
	useUserDiscard({ onDiscard: onCancel, ref: discardScreenRef });
	useStreamingScroll({ api, view: editorView, modalRef: discardScreenRef });

	return (
		<Container appearance={appearance} ref={discardScreenRef}>
			<DiscardScreen
				onCancel={onCancel}
				onDiscard={onDiscard}
				text={formatMessage(aiExperienceApplicationMessages.discardStreamingInDocumennt)}
				appearance={appearance}
				isAIFC={isInDocumentStreamingEnabled}
			/>
		</Container>
	);
}
